import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Empty, Layout, Row, Space, Spin, Statistic, Table, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined, EnvironmentOutlined, LoginOutlined, LogoutOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { useLanguage } from './LanguageContext';
import { useOwnerContext } from './OwnerContext';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';
import { getClinicDoctorOptions } from '../utils/clinicDoctors';
import { resolveAttendanceDoctorName } from '../utils/attendanceDoctorName';
import ClinicAttendanceZoneSettings from './ClinicAttendanceZoneSettings';
import MissingCheckoutCorrectionModal from './MissingCheckoutCorrectionModal';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface Log {
  _id: string; doctor_id: string; doctor_name: string; clinic_name: string;
  clinic_id: string; action: 'sign_in' | 'sign_out'; createdAt: string;
  distance_from_center_m?: number; needs_review?: boolean; review_status?: 'pending' | 'resolved';
  review_reason?: 'missing_sign_out' | 'missing_sign_in';
}

interface Session {
  key: string; doctorId: string; doctorName: string; clinicName: string;
  dateAt: string; signInAt: string | null; signOutAt: string | null; minutes: number | null;
  signInLogId: string; clinicId: string; needsReview: boolean;
  reviewReason: 'missing_sign_out' | 'missing_sign_in' | null;
}

function pairSessions(logs: Log[], doctorNames: Record<string, string>): Session[] {
  const grouped: Record<string, Log[]> = {};
  logs.forEach((log) => { (grouped[log.doctor_id] ||= []).push(log); });
  return Object.entries(grouped).flatMap(([doctorId, items]) => {
    const sorted = [...items].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
    const result: Session[] = [];
    let pending: Log | null = null;
    sorted.forEach((log) => {
      if (log.action === 'sign_in') {
        if (pending) result.push({ key: pending._id, doctorId, doctorName: resolveAttendanceDoctorName(doctorId, doctorNames, pending.doctor_name), clinicName: pending.clinic_name, dateAt: pending.createdAt, signInAt: pending.createdAt, signOutAt: null, minutes: null, signInLogId: pending._id, clinicId: pending.clinic_id, needsReview: pending.needs_review === true || dayjs(pending.createdAt).format('YYYY-MM-DD') < dayjs().format('YYYY-MM-DD'), reviewReason: 'missing_sign_out' });
        pending = log;
      } else if (pending) {
        const sameDay = dayjs(pending.createdAt).format('YYYY-MM-DD') === dayjs(log.createdAt).format('YYYY-MM-DD');
        result.push({ key: pending._id, doctorId, doctorName: resolveAttendanceDoctorName(doctorId, doctorNames, pending.doctor_name, log.doctor_name), clinicName: pending.clinic_name, dateAt: pending.createdAt, signInAt: pending.createdAt, signOutAt: sameDay ? log.createdAt : null, minutes: sameDay ? Math.max(0, Math.round((dayjs(log.createdAt).valueOf() - dayjs(pending.createdAt).valueOf()) / 60000)) : null, signInLogId: pending._id, clinicId: pending.clinic_id, needsReview: !sameDay || pending.needs_review === true, reviewReason: sameDay ? null : 'missing_sign_out' });
        if (!sameDay) result.push({ key: log._id, doctorId, doctorName: resolveAttendanceDoctorName(doctorId, doctorNames, log.doctor_name), clinicName: log.clinic_name, dateAt: log.createdAt, signInAt: null, signOutAt: log.createdAt, minutes: null, signInLogId: '', clinicId: log.clinic_id, needsReview: true, reviewReason: 'missing_sign_in' });
        pending = null;
      } else {
        result.push({ key: log._id, doctorId, doctorName: resolveAttendanceDoctorName(doctorId, doctorNames, log.doctor_name), clinicName: log.clinic_name, dateAt: log.createdAt, signInAt: null, signOutAt: log.createdAt, minutes: null, signInLogId: '', clinicId: log.clinic_id, needsReview: true, reviewReason: 'missing_sign_in' });
      }
    });
    const openSession = pending as Log | null;
    if (openSession) result.push({ key: openSession._id, doctorId, doctorName: resolveAttendanceDoctorName(doctorId, doctorNames, openSession.doctor_name), clinicName: openSession.clinic_name, dateAt: openSession.createdAt, signInAt: openSession.createdAt, signOutAt: null, minutes: null, signInLogId: openSession._id, clinicId: openSession.clinic_id, needsReview: openSession.needs_review === true || dayjs(openSession.createdAt).format('YYYY-MM-DD') < dayjs().format('YYYY-MM-DD'), reviewReason: 'missing_sign_out' });
    return result;
  }).sort((a, b) => dayjs(b.dateAt).valueOf() - dayjs(a.dateAt).valueOf());
}

const duration = (minutes: number | null) => minutes == null ? '—' : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const DoctorAttendanceManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { doctorId, doctorName } = useDoctorContext();
  const { selectedClinic, selectedClinicId } = useClinicContext();
  const { canSeeDoctorAttendanceReport, canManageOwnerFeatures } = useOwnerContext();
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [latest, setLatest] = useState<Log | null>(null);
  const [logs, setLogs] = useState<Log[]>([]);
  const [doctorNames, setDoctorNames] = useState<Record<string, string>>({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [staleOpenSession, setStaleOpenSession] = useState<Log | null>(null);
  const [correctionTarget, setCorrectionTarget] = useState<Session | null>(null);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('day')]);

  const clinicId = selectedClinicId === '__all_clinics__' ? null : selectedClinicId;

  const loadStatus = useCallback(async () => {
    if (!doctorId || !clinicId) return;
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_ATTENDANCE_STATUS(doctorId)}`, { params: { clinic_id: clinicId } });
      setIsCheckedIn(res.data?.is_checked_in === true);
      setLatest(res.data?.latest || null);
      setStaleOpenSession(res.data?.stale_open_session || null);
    } catch { message.error(t('doctorAttendanceLoadFailed')); }
    finally { setStatusLoading(false); }
  }, [clinicId, doctorId, t]);

  const loadLogs = useCallback(async () => {
    if (!canSeeDoctorAttendanceReport || !clinicId) return;
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_ATTENDANCE_LOGS}`, { params: {
        clinic_id: clinicId, from: range[0].startOf('day').toISOString(), to: range[1].endOf('day').toISOString(), limit: 3000,
      } });
      setLogs(res.data?.logs || []);
    } catch { message.error(t('doctorAttendanceLoadFailed')); }
    finally { setLogsLoading(false); }
  }, [canSeeDoctorAttendanceReport, clinicId, range, t]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    if (!clinicId) {
      setDoctorNames({});
      return;
    }
    let active = true;
    getClinicDoctorOptions(clinicId, doctorId, doctorName).then((doctors) => {
      if (active) setDoctorNames(Object.fromEntries(doctors.map((doctor) => [doctor.id, doctor.name])));
    });
    return () => { active = false; };
  }, [clinicId, doctorId, doctorName]);

  const submit = (action: 'sign_in' | 'sign_out') => {
    if (!doctorId || !clinicId || !selectedClinic) return message.warning(t('doctorAttendanceChooseClinic'));
    if (!navigator.geolocation) return message.error(t('attendanceGeoNotSupported'));
    setActionLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const ownerId = await getClinicOwnerDoctorId(clinicId, doctorId);
        const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_ATTENDANCE_CHECK}`, {
          doctor_id: doctorId, doctor_name: resolveAttendanceDoctorName(doctorId, doctorNames, doctorName), clinic_id: clinicId, clinic_name: selectedClinic.name,
          zone_owner_doctor_id: ownerId, action,
          latitude: position.coords.latitude, longitude: position.coords.longitude,
          accuracy_meters: position.coords.accuracy,
          request_id: `${doctorId}-${clinicId}-${action}-${Date.now()}`,
        });
        message.success(action === 'sign_in'
          ? t('doctorAttendanceSignedIn')
          : response.data?.log?.review_reason === 'missing_sign_in'
            ? t('attendanceMissingCheckinRecorded')
            : t('doctorAttendanceSignedOut'));
        await Promise.all([loadStatus(), loadLogs()]);
      } catch (error: any) {
        message.error(error?.response?.data?.message || t('doctorAttendanceActionFailed'));
      } finally { setActionLoading(false); }
    }, (error) => {
      setActionLoading(false);
      const errorMessage = error.code === 1
        ? t('attendanceGeoPermissionDenied')
        : error.code === 2
          ? t('attendanceGeoUnavailable')
          : error.code === 3
            ? t('attendanceGeoTimeout')
            : t('attendanceGeoDenied');
      message.error(errorMessage);
    }, { enableHighAccuracy: true, timeout: 15000 });
  };

  const submitCorrection = async (correctedSignOutAt: string, note: string) => {
    if (!correctionTarget || !clinicId || !doctorId) return;
    setCorrectionLoading(true);
    try {
      const ownerId = await getClinicOwnerDoctorId(clinicId, doctorId) || doctorId;
      await axios.patch(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_ATTENDANCE_CORRECT_MISSING_CHECKOUT(ownerId, clinicId, correctionTarget.signInLogId)}`,
        { corrected_sign_out_at: correctedSignOutAt, correction_note: note }
      );
      message.success(t('attendanceCorrectionSaved'));
      setCorrectionTarget(null);
      await Promise.all([loadStatus(), loadLogs()]);
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('error'));
    } finally {
      setCorrectionLoading(false);
    }
  };

  const sessions = useMemo(() => pairSessions(logs, doctorNames), [doctorNames, logs]);
  const totalMinutes = sessions.reduce((sum, item) => sum + (item.minutes || 0), 0);
  const doctorCount = new Set(logs.map((log) => log.doctor_id)).size;

  const columns = [
    { title: t('doctorAttendanceDoctor'), dataIndex: 'doctorName', key: 'doctorName', render: (v: string) => v || '—' },
    { title: t('attendanceClinic'), dataIndex: 'clinicName', key: 'clinicName' },
    { title: t('attendanceDate'), dataIndex: 'dateAt', key: 'date', render: (v: string) => dayjs(v).format('DD/MM/YYYY') },
    { title: t('attendanceSignInTime'), dataIndex: 'signInAt', key: 'in', render: (v: string | null) => v ? dayjs(v).format('hh:mm A') : <Tag color="orange">{t('attendanceMissingCheckin')}</Tag> },
    { title: t('attendanceSignOutTime'), dataIndex: 'signOutAt', key: 'out', render: (v: string | null, row: Session) => v ? dayjs(v).format('hh:mm A') : <Space wrap><Tag color={row.needsReview ? 'orange' : 'blue'}>{row.needsReview ? t('attendanceMissingCheckout') : t('attendanceStillCheckedIn')}</Tag>{canManageOwnerFeatures && <Button size="small" onClick={() => setCorrectionTarget(row)}>{t('attendanceCorrectMissingCheckout')}</Button>}</Space> },
    { title: t('attendanceDuration'), dataIndex: 'minutes', key: 'duration', render: duration },
  ];

  return <Layout style={{ minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
    <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} />
      <Title level={3} style={{ margin: 0 }}>{t('doctorAttendanceTitle')}</Title>
    </Header>
    <Content style={{ padding: 24, maxWidth: 1200, width: '100%', margin: '0 auto' }}>
      {!clinicId && <Alert type="info" showIcon message={t('doctorAttendanceChooseClinic')} style={{ marginBottom: 16 }} />}
      <Card style={{ borderRadius: 16, marginBottom: 20 }}>
        <Row gutter={[24, 20]} align="middle">
          <Col xs={24} md={14}>
            <Space direction="vertical" size={8}>
              <Text type="secondary">{t('doctorAttendanceMyStatus')}</Text>
              <Space><Tag color={isCheckedIn ? 'green' : 'default'}>{isCheckedIn ? t('doctorAttendanceInside') : t('doctorAttendanceOutside')}</Tag><Text strong>{selectedClinic?.name || '—'}</Text></Space>
              {latest && <Text type="secondary">{t('doctorAttendanceLastAction')}: {dayjs(latest.createdAt).format('DD/MM/YYYY hh:mm A')}</Text>}
              <Text type="secondary"><EnvironmentOutlined /> {t('doctorAttendanceLocationRequired')}</Text>
            </Space>
          </Col>
          <Col xs={24} md={10}>
            <Spin spinning={statusLoading || actionLoading}>
              <Space style={{ width: '100%' }} direction="vertical">
                <Button block size="large" type="primary" icon={<LoginOutlined />} disabled={!clinicId || isCheckedIn} onClick={() => submit('sign_in')}>{t('attendanceSignIn')}</Button>
                <Button block size="large" danger icon={<LogoutOutlined />} disabled={!clinicId || statusLoading || latest?.action === 'sign_out'} onClick={() => submit('sign_out')}>{t('attendanceSignOut')}</Button>
              </Space>
            </Spin>
          </Col>
        </Row>
      </Card>

      {staleOpenSession && (
        <Alert
          type="warning"
          showIcon
          message={t('attendanceMissingCheckout')}
          description={`${dayjs(staleOpenSession.createdAt).format('DD/MM/YYYY HH:mm')} — ${staleOpenSession.doctor_name || doctorName}`}
          style={{ marginBottom: 20 }}
        />
      )}

      {clinicId && <ClinicAttendanceZoneSettings clinicId={clinicId} />}

      {canSeeDoctorAttendanceReport && <Card title={<Space><TeamOutlined />{t('doctorAttendanceReport')}</Space>} extra={<Button icon={<ReloadOutlined />} onClick={loadLogs}>{t('attendanceRefreshLogs')}</Button>} style={{ borderRadius: 16 }}>
        <Space wrap style={{ marginBottom: 18 }}><RangePicker value={range} onChange={(v) => v?.[0] && v?.[1] && setRange([v[0], v[1]])} /></Space>
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={12} md={6}><Card size="small"><Statistic title={t('doctorAttendanceDoctors')} value={doctorCount} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title={t('attendanceSessions')} value={sessions.length} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title={t('attendanceTotalHours')} value={(totalMinutes / 60).toFixed(1)} suffix="h" /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title={t('doctorAttendanceOpenSessions')} value={sessions.filter((s) => s.signOutAt == null).length} /></Card></Col>
        </Row>
        <Table loading={logsLoading} columns={columns} dataSource={sessions} scroll={{ x: 760 }} locale={{ emptyText: <Empty description={t('doctorAttendanceNoLogs')} /> }} pagination={{ pageSize: 20, showSizeChanger: false }} />
      </Card>}
      <MissingCheckoutCorrectionModal
        open={Boolean(correctionTarget)}
        signInAt={correctionTarget?.signInAt || null}
        loading={correctionLoading}
        onCancel={() => setCorrectionTarget(null)}
        onSubmit={submitCorrection}
      />
    </Content>
  </Layout>;
};

export default DoctorAttendanceManagement;
